/**
 * Debug overlay for the `audio_debug` shot.
 *
 * The critic cannot listen, so this draws what the numbers say: per-sound waveform
 * envelope, log-frequency spectrum, and the measured peak / attack / centroid /
 * RT60. If the spectra look plausible and the panel is populated, the audio system
 * is demonstrably alive and synthesising.
 */

const CARD_W = 316;
const CARD_H = 150;
const PAD = 14;
const DB_RANGE = 78;   // display range under each sound's own peak

export class AudioDebugOverlay {
  constructor() {
    this.canvas = null;
    this.items = [];
    this.status = {};
    this.visible = false;
    this.progress = { done: 0, total: 0 };
  }

  _ensure() {
    if (this.canvas || typeof document === 'undefined') return this.canvas;
    const c = document.createElement('canvas');
    c.id = 'audio-debug-overlay';
    c.style.cssText = 'position:fixed;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:9999;display:none;';
    document.body.appendChild(c);
    this.canvas = c;
    return c;
  }

  show(on) {
    const c = this._ensure();
    if (!c) return;
    this.visible = !!on;
    c.style.display = on ? 'block' : 'none';
    if (on) this.draw();
  }

  setItems(items) {
    this.items = items || [];
    if (this.visible) this.draw();
  }

  pushItem(item) {
    this.items.push(item);
    this.progress.done = this.items.length;
    if (this.visible) this.draw();
  }

  setStatus(s) {
    this.status = s || {};
  }

  draw() {
    const c = this._ensure();
    if (!c || !this.visible) return;
    const w = Math.max(640, window.innerWidth || 1920);
    const h = Math.max(480, window.innerHeight || 1080);
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    const g = c.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, w, h);

    /* ---- backdrop: only behind the panel, so the live scene stays visible ---- */
    const cols0 = Math.max(1, Math.floor((w - PAD) / (CARD_W + PAD)));
    const rows0 = Math.max(1, Math.ceil(Math.max(1, this.items.length) / cols0));
    const panelH = Math.min(h, 116 + rows0 * (CARD_H + PAD) + 26);
    g.fillStyle = 'rgba(6,8,11,0.90)';
    g.fillRect(0, 0, w, panelH);
    const fade = g.createLinearGradient(0, panelH, 0, panelH + 90);
    fade.addColorStop(0, 'rgba(6,8,11,0.90)');
    fade.addColorStop(1, 'rgba(6,8,11,0)');
    g.fillStyle = fade;
    g.fillRect(0, panelH, w, 90);

    /* ---- header ---- */
    const s = this.status;
    g.font = '600 22px ui-monospace, Menlo, monospace';
    g.fillStyle = '#e8f0ff';
    g.fillText('AUDIO — procedural synthesis diagnostics', PAD, 34);

    g.font = '13px ui-monospace, Menlo, monospace';
    g.fillStyle = '#8fb4d9';
    const line1 = [
      `context: ${s.contextState || 'not created'}`,
      `enabled: ${s.enabled ? 'yes' : 'no (awaiting user gesture)'}`,
      `sr: ${s.sampleRate || 48000} Hz`,
      `voices: ${s.voices ?? 0}/${s.maxVoices ?? 0}`,
      `baked: ${s.baked ?? 0}`,
      `bakes pending: ${s.pending ?? 0}`,
    ].join('   ');
    g.fillText(line1, PAD, 56);
    const zw = s.zones && s.zones.weights ? s.zones.weights : null;
    const line2 = zw
      ? `zone: ${s.zones.current}   outdoor ${zw.outdoor.toFixed(2)}  alley ${zw.alley.toFixed(2)}  `
        + `small ${zw.small_interior.toFixed(2)}  large ${zw.large_interior.toFixed(2)}   `
        + `ceiling ${Number(s.zones.ceiling).toFixed(1)} m  mean free path ${Number(s.zones.meanDistance).toFixed(1)} m`
      : 'zone: (not initialised — audio never resumed)';
    g.fillText(line2, PAD, 76);
    g.fillText(
      `buses: weapons / world / ui / music / verb → limiter    music: ${s.music || 'idle'}    `
      + `events: fire ${s.eventCounts?.fire ?? 0}  hit ${s.eventCounts?.hit ?? 0}  step ${s.eventCounts?.step ?? 0}`,
      PAD, 96,
    );

    if (!this.items.length) {
      g.fillStyle = '#ffb454';
      g.font = '16px ui-monospace, Menlo, monospace';
      g.fillText('rendering offline analysis…', PAD, 130);
      return;
    }

    /* ---- cards ---- */
    const cols = Math.max(1, Math.floor((w - PAD) / (CARD_W + PAD)));
    const top = 116;
    this.items.forEach((it, i) => {
      const cx = PAD + (i % cols) * (CARD_W + PAD);
      const cy = top + Math.floor(i / cols) * (CARD_H + PAD);
      if (cy + CARD_H > h) return;
      this._card(g, cx, cy, it);
    });

    g.fillStyle = '#5d7898';
    g.font = '12px ui-monospace, Menlo, monospace';
    g.fillText(
      'top strip = peak waveform envelope · bottom = log-frequency spectrum (25 Hz → 20 kHz, 90 dB range)',
      PAD, Math.min(h - 12, top + Math.ceil(this.items.length / cols) * (CARD_H + PAD) + 4),
    );
  }

  _card(g, x, y, it) {
    g.fillStyle = 'rgba(20,26,34,0.95)';
    g.fillRect(x, y, CARD_W, CARD_H);
    g.strokeStyle = it.isIR ? '#3d6ea8' : '#2c3a4c';
    g.lineWidth = 1;
    g.strokeRect(x + 0.5, y + 0.5, CARD_W - 1, CARD_H - 1);

    g.font = '600 13px ui-monospace, Menlo, monospace';
    g.fillStyle = it.isIR ? '#7fc6ff' : '#d8e6f5';
    g.fillText(String(it.label).slice(0, 30), x + 8, y + 17);

    /* waveform envelope */
    const wx = x + 8;
    const wy = y + 24;
    const ww = CARD_W - 16;
    const wh = 34;
    g.fillStyle = '#0c1016';
    g.fillRect(wx, wy, ww, wh);
    const wave = it.wave || [];
    g.strokeStyle = '#4fd6a0';
    g.beginPath();
    for (let i = 0; i < wave.length; i++) {
      const px = wx + (i / Math.max(1, wave.length - 1)) * ww;
      const a = Math.min(1, wave[i]);
      g.moveTo(px, wy + wh / 2 - (a * wh) / 2);
      g.lineTo(px, wy + wh / 2 + (a * wh) / 2);
    }
    g.stroke();
    g.strokeStyle = '#1e2a38';
    g.beginPath();
    g.moveTo(wx, wy + wh / 2); g.lineTo(wx + ww, wy + wh / 2);
    g.stroke();

    /* spectrum */
    const sx = wx;
    const sy = wy + wh + 6;
    const sw = ww;
    const sh = 44;
    g.fillStyle = '#0c1016';
    g.fillRect(sx, sy, sw, sh);
    const spec = it.spec || [];
    const bw = sw / Math.max(1, spec.length);
    // normalise to each sound's own spectral peak so the SHAPE is readable —
    // absolute level is already reported numerically underneath
    let top = -1e9;
    for (let i = 0; i < spec.length; i++) if (spec[i] > top) top = spec[i];
    for (let i = 0; i < spec.length; i++) {
      const db = spec[i] - top;
      const v = Math.max(0, Math.min(1, (db + DB_RANGE) / DB_RANGE));
      const bh = v * sh;
      const hue = 205 - v * 165;
      g.fillStyle = `hsl(${hue},72%,${28 + v * 34}%)`;
      g.fillRect(sx + i * bw, sy + sh - bh, Math.max(1, bw - 0.6), bh);
    }
    // octave gridlines at 100 Hz / 1 kHz / 10 kHz
    g.strokeStyle = 'rgba(120,150,190,0.28)';
    for (const f of [100, 1000, 10000]) {
      const t = (Math.log(f) - Math.log(25)) / (Math.log(20000) - Math.log(25));
      g.beginPath();
      g.moveTo(sx + t * sw, sy); g.lineTo(sx + t * sw, sy + sh);
      g.stroke();
    }

    /* numbers */
    g.font = '11px ui-monospace, Menlo, monospace';
    g.fillStyle = '#9db8d4';
    const dbfs = (v) => (v > 0 ? (20 * Math.log10(v)).toFixed(1) : '-inf');
    const l1 = it.isIR
      ? `RT60 ${it.rt60.toFixed(2)}s  target ${it.intended.toFixed(2)}s  len ${it.seconds.toFixed(2)}s`
      : `peak ${dbfs(it.peak)}dB  rms ${dbfs(it.rms)}dB  clipped ${it.clipped}`;
    const l2 = it.isIR
      ? `centroid ${Math.round(it.centroid)}Hz   LF 40-160Hz ${(it.lowEnergy * 100).toFixed(0)}%`
      : `attack ${it.attackMs.toFixed(2)}ms  centroid ${Math.round(it.centroid)}Hz  LF ${(it.lowEnergy * 100).toFixed(0)}%`;
    g.save();
    g.beginPath();
    g.rect(x + 6, sy + sh + 2, CARD_W - 12, 30);
    g.clip();
    g.fillText(l1, x + 8, sy + sh + 14);
    g.fillText(l2, x + 8, sy + sh + 27);
    g.restore();
  }

  dispose() {
    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
    this.canvas = null;
  }
}
